<template>
  <div class="drawer" :class="visible ? '' : 'hidden'">
    <div class="overlay" :class="visible ? '' : 'hidden'" @click="closeCart"></div>
    <div class="cart-pane" :class="visible ? 'pane-open' : 'pane-close'">
      <div class="flex justify-between mb-8 font-semibold">
        <div>{{ textSnippets.basket || 'Warenkorb' }}</div>
        <div class="cursor-pointer uppercase" @click="closeCart">
          <span> {{ textSnippets.back }} </span>
          <hr />
        </div>
      </div>
      <div>
        <div>
          {{ textSnippets.your_basket_has }} {{ products.length }} {{ textSnippets.products }}
          <hr class="mt-4 text-dark-grey" />
        </div>
        <div>
          <cart-item
            v-for="item in products"
            :key="item.id"
            v-bind="item"
            :locale="textSnippets.locale"
            @remove="handelRemoveItem"
          />
        </div>
      </div>
      <div class="flex justify-between mb-8 font-semibold mt-4">
        <div>{{ textSnippets.totalPrice }}</div>
        <div class="cursor-pointer mr-8">{{ totalPrice }}</div>
      </div>
      <feature name="send-participant-information-to-shop">
        <div class="mt-10">
          <base-checkbox
            v-model:selectedItem="userConfirmation"
            :value="1"
            :uuid="textSnippets.link_approval || 0"
            :checked="userConfirmationStore"
            class="py-2 px-6 col-frist"
          >
            <span class="text-sm">{{ textSnippets.link_approval }}</span>
          </base-checkbox>
          <base-text class="mt-4 px-6 text-sm">
            {{ textSnippets.data_protection_info }}
            <br />
            <a :href="urls.data_protection ?? '#'" target="_blank" class="text-sm">{{
              textSnippets.data_protection
            }}</a>
          </base-text>
        </div>
      </feature>
      <div class="flex justify-end mt-10">
        <base-button class="mr-4 uppercase px-5 md:px-10" @click="closeCart">{{ textSnippets.back }}</base-button>
        <base-button
          :type="'primary'"
          class="checkout-button px-5 md:px-10"
          :disabled="couldCheckout"
          :to="checkoutUrl"
          >{{ textSnippets.checkout }}</base-button
        >
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, inject, ref, watch } from 'vue'
import { FeatureToggleComponent as feature } from 'vue-feature-toggle'
import BaseButton from '@/components/base/BaseButton.vue'
import BaseCheckbox from '@/components/base/BaseCheckbox.vue'
import BaseText from '@/components/base/BaseText.vue'
import CartItem from './CartItem.vue'
import { useMainStore } from '@/store/main'
import utils from '@/utils'

export default defineComponent({
  components: { CartItem, BaseButton, BaseCheckbox, BaseText, feature },
  props: {
    visible: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, { emit }) {
    /**
     * register store
     */
    const store = useMainStore()

    /**
     * @returns {Language} textSnippets
     */
    const textSnippets = computed(() => store.snippets)

    /**
     * @returns {ICart} products
     */
    const products = computed(() => {
      return store.cart
    })

    /**
     * @returns String checkoutUrl
     */
    const checkoutUrl = computed(() => store.checkoutUrl)

    /**
     * @returns Boolean checkoutUrl
     */
    const checkoutUrlLoading = computed(() => store.checkoutUrlLoading)

    /**
     * @return {ref<Number>} userConfirmation
     */
    const userConfirmation = ref([])

    /**
     * @return {Urls} urls
     */
    const urls = computed(() => store.urls)

    /**
     * @returns {Boolean} userConfirmationStore
     */
    const userConfirmationStore = computed(() => {
      return store.userConfirmation
    })

    /**
     * @returns {Boolean} couldCheckout
     */
    const couldCheckout = computed(
      () => !((Object.keys(products.value).length && checkoutUrl.value && !checkoutUrlLoading.value) || false),
    )

    const config: any = inject('config')

    const handelRemoveItem = (id: number) => {
      store.removeFromCart(id)
      store.updateBackendCart(config)
    }

    /**
     * set setUserconfirmation true/false depending on userConfirmation
     */
    watch(userConfirmation, (newValue, oldValue) => {
      if (newValue !== oldValue) {
        if (newValue[0] === 1) {
          store.setUserconfirmation(true)
        } else {
          store.setUserconfirmation(false)
        }
        store.updateBackendCart(config)
      }
    })

    const totalPrice = computed(() => {
      let total = 0
      const listProducts: any = products.value

      for (const [key, value] of Object.entries(listProducts)) {
        total += listProducts[key].price
      }

      return utils.formatterCurrency(textSnippets.value.locale, currencyDecimalISO.value).format(total)
    })

    /**
     * returns currency decimal ISO
     */
    const currencyDecimalISO = computed(() => {
      const listProducts: any = products.value
      const firstProduct = listProducts[Object.keys(listProducts)[0]]

      return firstProduct?.currency || 'EUR'
    })

    /**
     * @description close cart
     */
    const closeCart = (): void => {
      store.closeCart()
    }
    return {
      handelRemoveItem,
      totalPrice,
      products,
      closeCart,
      textSnippets,
      checkoutUrl,
      checkoutUrlLoading,
      couldCheckout,
      userConfirmation,
      userConfirmationStore,
      urls,
    }
  },
})
</script>

<style scoped>
.cart-pane {
  @apply w-117 relative z-50 h-screen bg-white text-base p-4 transform overflow-y-auto transition duration-300 ease-in-out py-5;
}
.overlay {
  @apply w-full h-full absolute z-20 bg-black bg-opacity-40;
}
.checkout-button {
  @apply border-black;
}
.drawer {
  @apply w-screen h-screen fixed flex justify-end inset-0 z-20;
}

.pane-open {
  @apply translate-x-0;
}
.pane-close {
  @apply translate-x-117;
}
</style>
